import { useEffect, useState } from "react";
import { useLanguage } from "../../i18n/LanguageContext";
import { CITIES, INDUSTRIES, CITY_CURRENCY, CITY_DIALECT, defaultValue } from "../../data/roiData";
import { Reveal } from "../Reveal";
import { scrollToId } from "../../lib/scroll";
import { TrendingDown, Sparkles } from "lucide-react";

const RoiCalculator = () => {
  const { t, lang } = useLanguage();
  const [city, setCity] = useState("Riyadh");
  const [industry, setIndustry] = useState("Clinic");
  const [missed, setMissed] = useState(20);
  const [value, setValue] = useState(defaultValue("Riyadh", "Clinic"));

  useEffect(() => { setValue(defaultValue(city, industry)); }, [city, industry]);

  const currency = CITY_CURRENCY[city];
  const loss = (missed || 0) * (value || 0) * 30;
  const recovery = Math.round(loss * 0.7);
  const dialect = CITY_DIALECT[city]?.[lang] ?? CITY_DIALECT[city]?.en;

  return (
    <section id="calculator" data-testid="roi-section" className="relative mx-auto max-w-7xl scroll-mt-28 px-6 py-24 lg:px-10 lg:py-32">
      <div className="pointer-events-none absolute end-0 top-0 h-96 w-96 rounded-full bg-neon-muted blur-[140px]" />
      <Reveal>
        <p className="overline-tag">{t.roi.overline}</p>
        <h2 className="mt-4 font-display text-3xl font-extrabold md:text-5xl">{t.roi.title}</h2>
        <p className="mt-4 max-w-xl text-sm text-zinc-400 md:text-base">{t.roi.sub}</p>
      </Reveal>

      <div className="mt-12 grid gap-8 lg:grid-cols-12">
        <Reveal delay={0.1} className="lg:col-span-7">
          <div className="glass-panel grid gap-6 rounded-3xl p-8 sm:grid-cols-2" data-testid="roi-inputs">
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-zinc-500">{t.roi.city}</label>
              <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="calc-city" className="field-input">
                {CITIES.map((c) => <option key={c} value={c}>{t.roi.cities[c]}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-zinc-500">{t.roi.industry}</label>
              <select value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="calc-industry" className="field-input">
                {INDUSTRIES.map((i) => <option key={i} value={i}>{t.roi.industries[i]}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-zinc-500">{t.roi.missed}</label>
              <input type="number" min="0" value={missed} onChange={(e) => setMissed(parseInt(e.target.value) || 0)} data-testid="calc-missed" className="field-input" />
            </div>
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-zinc-500">
                {t.roi.value} ({currency})
              </label>
              <input type="number" min="0" value={value} onChange={(e) => setValue(parseInt(e.target.value) || 0)} data-testid="calc-value" className="field-input" />
            </div>

            <div className="sm:col-span-2 rounded-2xl border border-red-500/20 bg-red-500/5 p-6">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-red-400">
                <TrendingDown size={14} /> {t.roi.loss}
              </div>
              <p className="mt-2 font-display text-4xl font-extrabold text-red-300 md:text-5xl" data-testid="calc-loss">
                {loss.toLocaleString()} {currency}
              </p>
              <p className="mt-2 text-sm text-zinc-400" data-testid="calc-recovery">
                {t.roi.recovery} (<span className="font-bold text-neon">+{recovery.toLocaleString()} {currency}</span>)
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2} className="lg:col-span-5">
          <div className="tracing-beam rounded-3xl" data-testid="roi-proposal">
            <div className="tracing-inner" />
            <div className="relative p-8">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-gold" />
                <h3 className="font-display text-xl font-bold">{t.roi.proposalTitle}</h3>
              </div>
              <div className="mt-6 flex flex-col divide-y divide-white/5 text-sm">
                <div className="flex justify-between py-3"><span className="text-zinc-500">{t.roi.dialect}</span><span className="font-bold text-white" data-testid="prop-dialect">{dialect}</span></div>
                <div className="flex justify-between py-3"><span className="text-zinc-500">{t.roi.currency}</span><span className="font-bold text-white" data-testid="prop-currency">{currency}</span></div>
                <div className="flex justify-between py-3"><span className="text-zinc-500">{t.roi.trial}</span><span className="font-bold text-gold">{t.roi.trialValue}</span></div>
                <div className="flex justify-between py-3"><span className="text-zinc-500">{t.roi.after}</span><span className="font-bold text-white">{t.roi.afterValue}</span></div>
              </div>
              <button onClick={() => scrollToId("deploy")} data-testid="roi-deploy-cta" className="btn-gold mt-8 w-full">
                {t.roi.cta}
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default RoiCalculator;
